import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useContext, useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import { Container, Fab, IconButton, Paper, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { TrackingCategory, trackingService, TrackingTranscriptionParams } from '../../../services';
import {
  DEMO_ESTIMATED_DURATION_SEC,
  DEMO_YT_ESTIMATED_DURATION_SEC,
  FULL_ESTIMATED_DURATION_SEC,
  FULL_YT_ESTIMATED_DURATION_SEC,
  scrollToSection,
  updateQueryStringParameter,
} from '../../../utils';

import { MAX_FILE_SIZE_IN_BYTE } from '../../../constants';
import type { WithUser } from '../../../context';
import { AppConstantsContext, TranscriptionStoreContext, withUserContext } from '../../../context';
import type { TranscriptionState } from '../../../entities';
import { JobType } from '../../../entities';
import type { ITheme } from '../../../theme';
import { FileDrop, TranscriptionProgress, VideoInputComponent } from '../../molecules';
import { AdminUrlBox } from '../../atoms';

import { useProcessing } from '../../../context/ProcessingContext/ProcessingContext';
import type { TranscriptionDialogContextValue } from '../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import withTranscriptionDialogContextProvider from '../../../context/TranscriptionDialogContext/withTranscriptionDialogContextProvider';
import useLocaleUrl from '../../../hooks/useLocaleUrl';
import { RecordingDialog } from '../Dialogs';
import TranscriptionDialog from '../Dialogs/TranscriptionDialog/TranscriptionDialog';
import TranscriptionDialogScan from '../Dialogs/TranscriptionDialog/TranscriptionDialogScan';

const useStyles = makeStyles<{ additionalProps: { asDrawer: boolean } }>()(
  (theme: ITheme, { additionalProps }) => ({
    cardTitle: {
      marginTop: 0,
      fontSize: '2.25rem',
      lineHeight: '1.4em',
      marginBottom: 10,
      color: '#fff',
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.25rem',
      },
    },
    cardSubTitle: {
      color: '#fff',
      marginTop: 0,
      lineHeight: '1.1em',
      marginBottom: 20,
    },
    card: {
      position: 'relative',
      boxShadow: additionalProps.asDrawer
        ? 'none'
        : `0px 0px 6px 2px ${theme.palette.primary.main}`,
      width: additionalProps.asDrawer ? '100%' : '80%',
      textAlign: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: '1.25rem',
      [theme.breakpoints.down('md')]: {
        padding: '1.25rem 6px',
      },
      '@media screen and (max-width:767px)': {
        width: '100%',
        marginLeft: 0,
        marginRight: 0,
      },
    },
    horizDivider: {
      marginBottom: 20,
      borderBottom: '1px solid #d3d3d3',
    },
    or: {
      position: 'relative',
      zIndex: 2,
      padding: '0 11px',
      top: 13,
      backgroundColor: '#343232',
      color: '#fff',
      fontSize: 18,
      borderRadius: '50%',
      textTransform: 'uppercase',
    },
    recordContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      alignItems: 'center',
    },
    recordButton: {
      marginTop: '0.5rem',
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
    },
    close: {
      position: 'absolute',
      top: '.8em',
      right: '1em',
      color: '#FFFFFF',
      [theme.breakpoints.down('sm')]: {
        right: '-1em',
        top: '0',
      },
    },
  })
);

export interface FileTypeResult {
  ext: string;
  mime: string;
}

export interface InputContainerProps extends WithUser, TranscriptionDialogContextValue {
  another?: boolean;
  baseUrl?: string;
  wordpress?: boolean;
  handleCloseModal?: () => void;
  noProcessingView?: boolean;
  asDrawer?: boolean;
  title?: string;
  subtitle?: string;
}

const InputContainer: React.FC<InputContainerProps> = ({
  baseUrl,
  wordpress,
  user,
  refresh,
  another,
  apriori,
  videoUrl,
  file,
  source,
  handleCloseModal,
  noProcessingView,
  asDrawer,
  title,
  subtitle,
}) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [recordDialogOpen, setRecordDialogOpen] = useState<boolean>(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const { updateTranscriptions } = useContext(TranscriptionStoreContext);
  const { jobType } = useContext(AppConstantsContext);
  const { t } = useTranslation();
  const localeUrl = useLocaleUrl();
  const { classes } = useStyles({ additionalProps: { asDrawer } });
  const trackingLocation = wordpress
    ? TrackingTranscriptionParams.location.wp
    : TrackingTranscriptionParams.location.songbook;

  const { jobs, startPolling, setEstimatedDuration } = useProcessing();
  const inputTitle = title ?? t('home.input.try_yourself');
  const inputSubtitle = subtitle ?? t('home.input.seconds_for_free');
  // Get the current job state
  const jobState = currentJobId ? jobs[currentJobId] : undefined;

  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('md'));

  const analysisStarted = (jobId: string) => {
    setCurrentJobId(jobId);

    if (!wordpress) {
      window.onbeforeunload = () => {};
      window.history.pushState({}, '', updateQueryStringParameter('job', jobId));
    }

    if (videoUrl) {
      setEstimatedDuration(
        jobId,
        user?.tickets ? FULL_YT_ESTIMATED_DURATION_SEC : DEMO_YT_ESTIMATED_DURATION_SEC
      );
    } else if (file) {
      setEstimatedDuration(
        jobId,
        user?.tickets ? FULL_ESTIMATED_DURATION_SEC : DEMO_ESTIMATED_DURATION_SEC
      );
    }

    startPolling(jobId);
    if (user && !wordpress) {
      updateTranscriptions();
    }
    if (user) {
      setTimeout(() => {
        refresh();
      }, 1500);
    }
    handleCloseModal?.();
  };

  useEffect(() => {
    setMounted(true);
    // const jobId = getParameterByName('job');
    // if (jobId) {
    //   setCurrentJobId(jobId);
    //   analysisStarted(jobId);
    // }
  }, []);

  useEffect(() => {
    if (jobState?.finishedData) {
      const trackingValues = {
        state: jobState.finishedData.state as TranscriptionState,
        input: videoUrl ? TrackingTranscriptionParams.input.youtube : source,
        type: user?.tickets
          ? TrackingTranscriptionParams.type.full
          : TrackingTranscriptionParams.type.free,
        location: trackingLocation,
      };
      trackingService.valueObjectEvent(
        TrackingCategory.Transcription,
        TrackingTranscriptionParams.action.finish,
        trackingValues
      );
      scrollToSection('container-progress');
      window.onbeforeunload = null;
      window.location.href = `${baseUrl ?? ''}${localeUrl(`/file?id=${jobState.finishedData.id}`)}`;
    }
  }, [jobState?.finishedData]);

  const onClickRecord = () => {
    const trackingValues = {
      location: trackingLocation,
      input: TrackingTranscriptionParams.input.microphone,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.start,
      trackingValues
    );
    setRecordDialogOpen(true);
  };

  const handleClose = () => {
    setEditDialogOpen(false);
    setRecordDialogOpen(false);
    if (handleCloseModal) {
      handleCloseModal();
    }
  };

  if (jobState?.processing && !jobState?.error && !noProcessingView) {
    return (
      <Container id="container-progress" maxWidth="md">
        <TranscriptionProgress
          title={t('home.input.transcribing', { title: apriori.title })}
          message={jobState.message}
          progress={jobState.progress}
          estimatedText={t('home.input.estimated')}
          processingTime={jobState.processingTime}
          estimatedDuration={jobState.estimatedDuration}
        />
      </Container>
    );
  }

  return (
    <Container id="cta" maxWidth="md">
      {user?.role === 'admin' && <AdminUrlBox />}
      <Paper
        className={classes.card}
        elevation={0}
        style={{ backgroundColor: asDrawer ? 'transparent' : '#343232' }}>
        {handleCloseModal && (
          <IconButton
            aria-label="Close transcription dialog"
            onClick={handleCloseModal}
            className={classes.close}>
            <CloseIcon />
          </IconButton>
        )}
        {!jobState?.error && (
          <Typography className={classes.cardTitle} variant="h3" component="h3">
            {another ? t('home.input.choose_another_song') : inputTitle}
          </Typography>
        )}
        {jobState?.error && (
          <Typography className={classes.cardTitle} variant="h3" component="h3" color="error">
            {t('home.input.transcription_error')}
          </Typography>
        )}

        {jobType === JobType.TranscribeScan ? (
          <>
            <Typography className={classes.cardSubTitle} variant="body1" component="p">
              {subtitle ?? t('home.input.measures_for_free')}
            </Typography>
            <div>
              <FileDrop
                location={trackingLocation}
                openDialog={() => setEditDialogOpen(true)}
                hint="(*.pdf, *.jpg, *.png)"
                allowMultipleFiles={false}
                maximumFileSizeBytes={MAX_FILE_SIZE_IN_BYTE}
              />
            </div>
          </>
        ) : (
          <>
            <Typography className={classes.cardSubTitle} variant="body1" component="p">
              {inputSubtitle}
            </Typography>
            <div>
              <FileDrop
                location={trackingLocation}
                openDialog={() => setEditDialogOpen(true)}
                hint="(*.wav, *.mp3, *.mp4, *.midi, *.flac, and more)"
                allowMultipleFiles={false}
                maximumFileSizeBytes={MAX_FILE_SIZE_IN_BYTE}
              />
            </div>
            <div className={classes.horizDivider}>
              <span className={classes.or}>{t('home.input.or')}</span>
            </div>

            {mounted && (
              <VideoInputComponent
                location={trackingLocation}
                openDialog={() => setEditDialogOpen(true)}
                label={
                  isMobile ? t('home.input.enter_youtube_mobile') : t('home.input.enter_youtube')
                }
              />
            )}
            <div className={classes.horizDivider}>
              <span className={classes.or}>{t('home.input.or')}</span>
            </div>
            <div className={classes.recordContainer}>
              <Fab
                color="primary"
                variant="extended"
                size="medium"
                className={classes.recordButton}
                onClick={onClickRecord}>
                <FiberManualRecordIcon fontSize="large" color="error" />
                {t('home.input.record_your_own')}
              </Fab>
            </div>
          </>
        )}
      </Paper>
      {editDialogOpen && jobType !== JobType.TranscribeScan && (
        <TranscriptionDialog
          open={editDialogOpen}
          handleClose={handleClose}
          handleProcessing={() => {}}
          handleResolve={analysisStarted}
          location={trackingLocation}
        />
      )}
      {editDialogOpen && jobType === JobType.TranscribeScan && (
        <TranscriptionDialogScan
          handleClose={handleClose}
          handleProcessing={() => {}}
          handleResolve={analysisStarted}
          location={trackingLocation}
        />
      )}
      {recordDialogOpen && (
        <RecordingDialog
          handleClose={() => setRecordDialogOpen(false)}
          handleResolve={() => {
            setRecordDialogOpen(false);
            setEditDialogOpen(true);
          }}
        />
      )}
    </Container>
  );
};

export default withUserContext(withTranscriptionDialogContextProvider(InputContainer));
